import * as Sentry from '@sentry/react';
import { configure } from 'mobx';
import { getAuthClient } from './auth/auth-client.js';
import config from './config.js';

if (import.meta.env.MODE === 'development') {
	configure({
		// enforceActions: 'always',
		// computedRequiresReaction: true,
		// reactionRequiresObservable: true,
		// observableRequiresReaction: true,
		// disableErrorBoundaries: true,
	});
}

if (config.sentryEnabled === true || config.sentryEnabled === 'true') {
	Sentry.init({
		dsn: config.sentryUrl,
	});
}

async function continueToApp() {
	const app = await import('./main');
	return app.default();
}

async function showSignInPage() {
	const signIn = await import('./main.signin');
	return signIn.default();
}

/**
 * Initialize better-auth client and check session
 */
async function initializeAuth() {
	// Handle auth errors passed through in the URL to prevent infinite redirect loops.
	// When SSO/OAuth fails (e.g., account_not_linked), the callback redirects here
	// with error= in the URL. Without this check, we'd redirect back to the IdP
	// and loop forever.
	const { search } = window.location;
	if (search.includes('error=')) {
		const params = new URLSearchParams(search);
		const errorCode = params.get('error');
		return await handleError(errorCode);
	}

	// Create better-auth client
	const betterAuthClient = getAuthClient();

	// Check if user has an active session
	// Use fetchOptions to ensure fresh check without caching
	const session = await betterAuthClient.getSession();

	if (session.data?.session) {
		// User is authenticated
		return await continueToApp();
	}

	// No session, attempt sign-in
	return await attemptSignin();
}

export async function attemptSignin() {
	const betterAuthClient = getAuthClient();

	const method = config.auth.method;
	if (method == null) {
		return await showSignInPage();
	}

	const providerId = config.auth.providerId;
	if (providerId == null) {
		throw new Error('Provider ID is required for SSO');
	}

	switch (method) {
		case 'sso': {
			const callbackURL =
				window.location.origin + (window.location.pathname || '/');
			try {
				const result = await betterAuthClient.signIn.sso({
					providerId,
					callbackURL,
				});

				if (result.error) {
					console.error('SSO sign-in failed:', result.error.message);
					return await handleError(result.error.code);
				}

				// signIn.sso() initiated a redirect to the IdP.
				// The browser will navigate away — just return and let it happen.
				return;
			} catch (ssoError) {
				console.error('SSO sign-in error:', ssoError);
				return await handleError(ssoError);
			}
		}
		default:
			throw new Error(
				`Unsupported auth method configured: ${config.auth.method}`
			);
	}
}

export async function handleError(err?: unknown) {
	const app = await import('./main.error');
	return app.default(err);
}

export async function initialize() {
	const pathname = window.location.pathname;
	if (pathname.startsWith('/error') || pathname.startsWith('/editor/error')) {
		return handleError();
	}

	try {
		return await initializeAuth();
	} catch (err) {
		// Log any errors to the console so we debug, but try to open the app anyways
		// The app itself should try to handle error states gracefully
		console.error('Bootstrap', err);
		return await continueToApp();
	}
}

window.addEventListener('load', initialize);
